import { applicationPhotoUrl, profilePhotoUrl, slug, stamp } from "./exportUpgrades";
import { buildZip, saveBlob, type ZipEntry } from "./zip";

/**
 * The applicants' photographs, downloaded as one zip.
 *
 * Induction paperwork — certificates, the induction booklet, access cards — is
 * built from faces, and the secretariat has been collecting them by opening
 * each application and right-clicking. This takes the same set the list is
 * already showing and hands it over in one go, named so the files can be used
 * without opening them: `Name_MembershipNo`.
 *
 * Two photographs per applicant, filed apart rather than merged:
 *   · Application Photos — the picture the application carries;
 *   · Profile Photos — the one on the applicant's account.
 * They are frequently different (an account avatar can be years old), and the
 * check the committee actually performs is one against the other. Filing them
 * in separate folders is also what lets both keep the plain `Name_MembershipNo`
 * name that was asked for, with no suffix to strip later.
 *
 * The two slots are found INDEPENDENTLY: an applicant with no account photo
 * still gets their application photo, and the other way round. The measure of
 * this export is the PDF listing next door — it prints a face for every fully
 * endorsed applicant, and so must this. That is why the application slot uses
 * the same fallback the PDF does rather than the row's own column, which most
 * applicants never write to (see applicationPhotoUrl in ./exportUpgrades).
 *
 * Where both slots resolve to the SAME file — common, because an applicant who
 * never re-uploaded shows their account photograph on the application — it is
 * downloaded once and written into both folders, so each folder stays one file
 * per applicant without paying for the image twice.
 *
 * The files are fetched from the API host's public storage, which already
 * answers with permissive CORS headers, so this needs nothing from the server.
 */

export type PhotoKind = "application" | "profile";

const FOLDER: Record<PhotoKind, string> = {
  application: "Application Photos",
  profile: "Profile Photos",
};

const KIND_LABEL: Record<PhotoKind, string> = {
  application: "Application photo",
  profile: "Profile photo",
};

export type PhotoJob = {
  applicationId: string | number;
  member: string;
  membershipNo: string;
  kind: PhotoKind;
  url: string;
  /** Path inside the archive, extension appended once the type is known. */
  path: string;
};

/** An applicant who has no photograph of a given kind on file at all. */
export type MissingPhoto = {
  applicationId: string | number;
  member: string;
  membershipNo: string;
  kind: PhotoKind;
};

/** A photograph that exists on the record but could not be downloaded. */
export type FailedPhoto = MissingPhoto & { url: string; reason: string };

export type PhotoPlan = {
  jobs: PhotoJob[];
  missing: MissingPhoto[];
  /** Applicants covered, whether or not they have both photographs. */
  applicants: number;
  /** Applicants with at least one photograph — what the PDF listing prints. */
  withAny: number;
  /** Applicants with nothing at all. The PDF shows a blank for these too. */
  withNone: number;
  /** Distinct files to download; lower than jobs when the two slots share one. */
  downloads: number;
};

export type PhotoExportReport = {
  filename: string;
  saved: number;
  failed: FailedPhoto[];
  missing: MissingPhoto[];
  bytes: number;
};

/* ── Naming ──────────────────────────────────────────────────────────────── */

/**
 * `Name_MembershipNo`, safe to write to disk on any of the three platforms the
 * secretariat uses.
 *
 * Spaces and case are kept — these files are read by people, and "ADEYEMI JOHN"
 * beats "adeyemi-john" on a contact sheet. Only what a filesystem genuinely
 * refuses is stripped: the reserved punctuation, control characters, and the
 * trailing dots/spaces Windows silently drops.
 */
export function photoFileBase(row: any): string {
  const name = String(row?.user?.name ?? "").trim() || "Unnamed applicant";
  const membershipNo = String(row?.user?.member?.membership_no ?? "").trim();

  const clean = (text: string) =>
    text
      // eslint-disable-next-line no-control-regex
      .replace(/[<>:"/\\|?*\u0000-\u001f]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      // Windows silently drops a trailing dot or space, so a name ending in one
      // would not be the name the file actually ends up carrying.
      .replace(/[. ]+$/, "");

  const base = clean(name);
  const no = clean(membershipNo);

  // No membership number is a real state (an applicant whose record predates
  // one). Better a file named after the person alone than one ending in a bare
  // underscore that reads like a truncation.
  return no ? `${base}_${no}` : base;
}

/* ── What would be exported ──────────────────────────────────────────────── */

export function planApplicantPhotos(rows: any[]): PhotoPlan {
  const jobs: PhotoJob[] = [];
  const missing: MissingPhoto[] = [];
  // One folder is one namespace: two members sharing a name AND lacking a
  // membership number would otherwise write over each other, and the archive
  // would come out short with nothing said about it.
  const taken: Record<PhotoKind, Set<string>> = {
    application: new Set(),
    profile: new Set(),
  };

  let withAny = 0;
  const urls = new Set<string>();

  for (const row of rows) {
    const member = String(row?.user?.name ?? "").trim() || "Unnamed applicant";
    const membershipNo = String(row?.user?.member?.membership_no ?? "").trim();
    const base = photoFileBase(row);

    const sources: Record<PhotoKind, string> = {
      application: applicationPhotoUrl(row),
      profile: profilePhotoUrl(row),
    };

    if (sources.application || sources.profile) withAny++;

    // Each slot stands on its own: a missing account photo must not cost the
    // applicant their application photo, nor the other way round.
    for (const kind of ["application", "profile"] as PhotoKind[]) {
      const url = sources[kind];

      if (!url) {
        missing.push({ applicationId: row.id, member, membershipNo, kind });
        continue;
      }

      urls.add(url);

      let name = base;
      for (let n = 2; taken[kind].has(name.toLowerCase()); n++) {
        name = `${base} (${n})`;
      }
      taken[kind].add(name.toLowerCase());

      jobs.push({
        applicationId: row.id,
        member,
        membershipNo,
        kind,
        url,
        path: `${FOLDER[kind]}/${name}`,
      });
    }
  }

  return {
    jobs,
    missing,
    applicants: rows.length,
    withAny,
    withNone: rows.length - withAny,
    downloads: urls.size,
  };
}

/* ── Downloading ─────────────────────────────────────────────────────────── */

/** What the file is, taken from the server rather than guessed from the URL. */
const EXTENSION_BY_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/bmp": "bmp",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/avif": "avif",
  "image/tiff": "tiff",
};

function extensionFor(contentType: string, url: string): string {
  const byType = EXTENSION_BY_TYPE[contentType.split(";")[0].trim().toLowerCase()];
  if (byType) return byType;

  const fromUrl = url.split("?")[0].split("#")[0].match(/\.([a-z0-9]{2,5})$/i);
  return fromUrl ? fromUrl[1].toLowerCase() : "jpg";
}

/** A downloaded file: the bytes, and what the server said it is. */
type Fetched = { data: Uint8Array; extension: string };

async function fetchOnce(url: string): Promise<Fetched> {
  const res = await fetch(url, { credentials: "omit" });

  if (!res.ok) {
    throw new Error(res.status === 404 ? "not found on the server" : `server said ${res.status}`);
  }

  const contentType = res.headers.get("content-type") ?? "";

  // A misconfigured host answers a missing file with its own HTML error page
  // and a 200. Writing that into the zip would produce an "image" that opens
  // as a broken file weeks later, with nothing to say why.
  if (contentType && !contentType.toLowerCase().startsWith("image/")) {
    throw new Error("the file is not an image");
  }

  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.length === 0) throw new Error("the file is empty");

  return { data: bytes, extension: extensionFor(contentType, url) };
}

/**
 * Fetch, and on failure try once more.
 *
 * Several requests are in flight at a time against a shared host, and a batch
 * that drops a face to a momentary hiccup is exactly the failure this export
 * exists to prevent. A genuine 404 fails twice and is reported; a blip does not
 * cost anybody their photograph.
 */
async function fetchPhoto(url: string): Promise<Fetched> {
  try {
    return await fetchOnce(url);
  } catch (first: any) {
    try {
      return await fetchOnce(url);
    } catch {
      throw first;
    }
  }
}

/**
 * Save ONE photograph, named the same way the bundle names it.
 *
 * Fetched into memory rather than linked to directly, because the `download`
 * attribute is ignored on a cross-origin href: a plain link to the storage host
 * would open the image in a tab, or save it under its random storage name
 * (`0G7bpH9vUc...jpg`), which is exactly the thing this is meant to avoid.
 *
 * Throws with a readable reason so the caller can offer the raw file instead.
 */
export async function downloadPhoto(url: string, fileBase: string): Promise<void> {
  const file = await fetchPhoto(url);
  saveBlob(new Blob([file.data as BlobPart], { type: "application/octet-stream" }), `${fileBase}.${file.extension}`);
}

/**
 * A short sheet inside the archive saying what it is — and, just as importantly,
 * what is NOT in it. An archive of 94 photographs handed over for a batch of 50
 * applicants has six gaps in it, and the person filing them needs to know whose
 * they are without counting.
 */
function manifest(
  saved: { entry: ZipEntry; job: PhotoJob }[],
  failed: FailedPhoto[],
  missing: MissingPhoto[],
  meta: { category: string },
): ZipEntry {
  const cell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const line = (cells: unknown[]) => cells.map(cell).join(",");

  // An applicant who never re-uploaded shows their account photograph on the
  // application, so the two files are one image. Saying so stops it being read
  // as a mistake — and stops anyone treating the pair as a verified match.
  const perApplicant = new Map<string, string[]>();
  for (const { job } of saved) {
    const key = String(job.applicationId);
    perApplicant.set(key, [...(perApplicant.get(key) ?? []), job.url]);
  }
  const isShared = ({ job }: { job: PhotoJob }) => {
    const urls = perApplicant.get(String(job.applicationId)) ?? [];
    return urls.length > 1 && urls.every((u) => u === urls[0]);
  };

  const lines = [
    line([`NIQS — Fully endorsed applicants' photographs, upgrades to ${meta.category}`]),
    line([`Taken on: ${stamp().printed}`]),
    line([`Photographs in this archive: ${saved.length}`]),
    ...(failed.length ? [line([`Could not be downloaded: ${failed.length}`])] : []),
    ...(missing.length ? [line([`None on file: ${missing.length}`])] : []),
    "",
    line(["Member", "Membership No", "Photograph", "File", "Note"]),
    ...saved.map((row) =>
      line([
        row.job.member,
        row.job.membershipNo,
        KIND_LABEL[row.job.kind],
        row.entry.name,
        isShared(row) ? "same image in both folders" : "",
      ]),
    ),
    ...failed.map((f) =>
      line([f.member, f.membershipNo, KIND_LABEL[f.kind], "", `not downloaded — ${f.reason}`]),
    ),
    ...missing.map((m) =>
      line([m.member, m.membershipNo, KIND_LABEL[m.kind], "", "none on file"]),
    ),
  ];

  // The BOM is what makes Excel read this as UTF-8 rather than the local
  // codepage — without it any accented name arrives as mojibake.
  return {
    name: "manifest.csv",
    data: new TextEncoder().encode("﻿" + lines.join("\r\n")),
  };
}

export function photoArchiveName(category: string): string {
  return `upgrade-photos-${slug(category)}-fully-endorsed-${stamp().file}.zip`;
}

/**
 * Fetch every planned photograph, bundle them, and hand the archive to the
 * browser. `onProgress` is called after each attempt so the dialog can show
 * where it has got to; a photograph that fails is recorded and the rest of the
 * batch carries on — one bad file must not cost the whole export.
 *
 * Downloads are keyed by URL, not by job: an applicant whose account photograph
 * is also the one on their application is one download that lands in both
 * folders.
 */
export async function exportApplicantPhotos(
  plan: PhotoPlan,
  meta: { category: string },
  onProgress?: (done: number, total: number) => void,
  concurrency = 5,
): Promise<PhotoExportReport> {
  const wanted = plan.jobs
    .map((j) => j.url)
    .filter((url, i, all) => all.indexOf(url) === i);
  const fetched = new Map<string, Fetched>();
  const errors = new Map<string, string>();

  let next = 0;
  let done = 0;

  const worker = async () => {
    for (let i = next++; i < wanted.length; i = next++) {
      const url = wanted[i];
      try {
        fetched.set(url, await fetchPhoto(url));
      } catch (e: any) {
        // A cross-origin fetch that is refused outright reports nothing useful,
        // so say what actually happened rather than "undefined".
        errors.set(url, e?.message ? String(e.message) : "the download was blocked");
      } finally {
        onProgress?.(++done, wanted.length);
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, wanted.length) }, worker));

  const saved: { entry: ZipEntry; job: PhotoJob }[] = [];
  const failed: FailedPhoto[] = [];

  for (const job of plan.jobs) {
    const file = fetched.get(job.url);
    if (file) {
      saved.push({ entry: { name: `${job.path}.${file.extension}`, data: file.data }, job });
    } else {
      failed.push({
        applicationId: job.applicationId,
        member: job.member,
        membershipNo: job.membershipNo,
        kind: job.kind,
        url: job.url,
        reason: errors.get(job.url) ?? "the download was blocked",
      });
    }
  }

  if (saved.length === 0) {
    throw new Error(
      failed.length
        ? "None of the photographs could be downloaded."
        : "There are no photographs to export.",
    );
  }

  // Sorted so the archive opens in a sensible order — the fetches finish out of
  // order, and an archive listed by whichever server responded first is not
  // something anyone can check against a register.
  saved.sort((a, b) => a.entry.name.localeCompare(b.entry.name, "en"));

  const sheet = manifest(saved, failed, plan.missing, meta);

  const blob = buildZip([...saved.map((s) => s.entry), sheet]);
  const filename = photoArchiveName(meta.category);
  saveBlob(blob, filename);

  return {
    filename,
    saved: saved.length,
    failed,
    missing: plan.missing,
    bytes: blob.size,
  };
}
